import { isOverdueItem } from '../../../../shared/utils/overdue';
import React from 'react';
import { Badge } from '@/shared/ui/badge';
import {
  ASSIGNMENT_STATUS_META,
  ASSIGNMENT_TYPE_META,
  ASSIGNMENT_URGENCY_META,
  INITIAL_ASSIGNMENT_FORM,
} from '@/modules/deals/utils/assignments/assignments.constants';

export function formatDate(dateString) {
  if (!dateString) return '—';

  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseAssignmentDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

export function isAssignmentOverdue(assignment = {}) {
  return isOperationalAssignment(assignment) && isOverdueItem(assignment);
}

export function isOperationalAssignment(assignment = {}) {
  return assignment.status !== 'rifiutato';
}

export function normalizeComparableId(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

export function idsMatch(left, right) {
  const normalizedLeft = normalizeComparableId(left);
  const normalizedRight = normalizeComparableId(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
}

export function includesComparableId(values = [], value) {
  const normalizedValue = normalizeComparableId(value);
  if (!normalizedValue || !Array.isArray(values)) return false;
  return values.some((item) => normalizeComparableId(item) === normalizedValue);
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function getCurrentUserCandidateIds(currentUser = {}) {
  return [
    currentUser.id,
    currentUser.userId,
    currentUser.linkedWorkspaceUserId,
    currentUser.workspaceUserId,
    ...(Array.isArray(currentUser.recipientIds) ? currentUser.recipientIds : []),
  ].map(normalizeComparableId).filter(Boolean);
}

function getCurrentUserStaffCandidateIds(currentUser = {}) {
  return [
    currentUser.staffId,
    ...(Array.isArray(currentUser.staffIds) ? currentUser.staffIds : []),
  ].map(normalizeComparableId).filter(Boolean);
}

function getCurrentUserCandidateNames(currentUser = {}) {
  return [
    currentUser.name,
    currentUser.fullName,
    currentUser.displayName,
    currentUser.email,
  ].map(normalizeText).filter(Boolean);
}

function getAssignmentRequesterIds(assignment = {}) {
  return [
    assignment.senderId,
    assignment.createdBy,
    assignment.createdByUserId,
    assignment.requesterId,
    assignment.requesterUserId,
    assignment.authorUserId,
  ].map(normalizeComparableId).filter(Boolean);
}

function getAssignmentWorkspaceAssignedIds(assignment = {}) {
  return [
    assignment.assigneeId,
    assignment.assignedTo,
    assignment.linkedWorkspaceUserId,
    assignment.workspaceUserId,
    assignment.responsibleId,
    ...(Array.isArray(assignment.recipientIds) ? assignment.recipientIds : []),
    ...(Array.isArray(assignment.assigneeIds) ? assignment.assigneeIds : []),
    ...(Array.isArray(assignment.responsibleIds) ? assignment.responsibleIds : []),
  ].map(normalizeComparableId).filter(Boolean);
}

function getAssignmentStaffAssignedIds(assignment = {}) {
  return [
    assignment.staffId,
    ...(Array.isArray(assignment.staffIds) ? assignment.staffIds : []),
  ].map(normalizeComparableId).filter(Boolean);
}

function getAssignmentAssignedNames(assignment = {}) {
  return [
    assignment.assignee,
    assignment.assigneeName,
    assignment.assignedTo,
    assignment.assignedToName,
    assignment.staffName,
    assignment.responsible,
    assignment.responsibleName,
    ...(Array.isArray(assignment.recipients) ? assignment.recipients : []),
    ...(Array.isArray(assignment.assignees) ? assignment.assignees : []),
    ...(Array.isArray(assignment.staffNames) ? assignment.staffNames : []),
  ].map(normalizeText).filter(Boolean);
}

export function isAssignmentRequestedByCurrentUser(assignment = {}, currentUser = {}) {
  const requesterIds = getAssignmentRequesterIds(assignment);
  return getCurrentUserCandidateIds(currentUser).some((candidateId) =>
    requesterIds.some((requesterId) => idsMatch(requesterId, candidateId))
  );
}

export function isAssignmentAssignedToCurrentUser(assignment = {}, currentUser = {}) {
  const currentUserIds = getCurrentUserCandidateIds(currentUser);
  const assignedIds = getAssignmentWorkspaceAssignedIds(assignment);

  if (currentUserIds.length > 0 && assignedIds.some((assignedId) =>
    currentUserIds.some((candidateId) => idsMatch(assignedId, candidateId))
  )) {
    return true;
  }

  const currentUserStaffIds = getCurrentUserStaffCandidateIds(currentUser);
  const assignedStaffIds = getAssignmentStaffAssignedIds(assignment);
  if (currentUserStaffIds.length > 0 && assignedStaffIds.some((assignedStaffId) =>
    currentUserStaffIds.some((candidateId) => idsMatch(assignedStaffId, candidateId))
  )) {
    return true;
  }

  if (assignedIds.length > 0 || assignedStaffIds.length > 0) return false;

  const currentUserNames = getCurrentUserCandidateNames(currentUser);
  const assignedNames = getAssignmentAssignedNames(assignment);
  return currentUserNames.length > 0 && assignedNames.some((assignedName) => currentUserNames.includes(assignedName));
}

export function isAssignmentRequestedOnlyByCurrentUser(assignment = {}, currentUser = {}) {
  return (
    isAssignmentRequestedByCurrentUser(assignment, currentUser) &&
    !isAssignmentAssignedToCurrentUser(assignment, currentUser)
  );
}

export function isSelfAssignedTask(assignment = {}, currentUser = {}) {
  const ids = getAssignmentWorkspaceAssignedIds(assignment);
  const userIds = getCurrentUserCandidateIds(currentUser);
  return isAssignmentRequestedByCurrentUser(assignment, currentUser) && ids.length > 0 &&
    ids.every((id) => userIds.some((userId) => idsMatch(id, userId)));
}

export function getStatusBadge(status) {
  const meta = ASSIGNMENT_STATUS_META[status];

  if (!meta) {
    return <Badge>{status}</Badge>;
  }

  return <Badge className={meta.badgeClass}>{meta.label}</Badge>;
}

export function getTypeBadge(type) {
  const meta = ASSIGNMENT_TYPE_META[type];

  if (!meta) {
    return <Badge variant="outline">{type}</Badge>;
  }

  return (
    <Badge variant="outline" className={meta.badgeClass}>
      {meta.label}
    </Badge>
  );
}

export function getUrgencyClass(urgency) {
  return ASSIGNMENT_URGENCY_META[urgency]?.badgeClass || 'bg-[var(--surface-raised)] text-[var(--on-primary)]';
}

export function getUrgencyLabel(urgency) {
  return ASSIGNMENT_URGENCY_META[urgency]?.label || urgency;
}

export function getAssignmentTypeMeta(type) {
  return ASSIGNMENT_TYPE_META[type] || ASSIGNMENT_TYPE_META.altro;
}

export function getResetAssignmentForm() {
  return { ...INITIAL_ASSIGNMENT_FORM, attachments: [] };
}

export function validateAssignmentForm(form) {
  const errors = {};

  if (!form.title.trim()) {
    errors.title = 'Il titolo è obbligatorio';
  }

  if (!form.description.trim()) {
    errors.description = 'La descrizione è obbligatoria';
  }

  if (!form.recipientIds.length) {
    errors.recipientIds = 'Seleziona almeno un destinatario';
  }

  const hasClient = Boolean(String(form.clientId || '').trim());
  const hasSupplier = Boolean(String(form.supplierId || '').trim());
  if (hasClient && hasSupplier) {
    errors.linkType = 'Seleziona cliente o fornitore, non entrambi';
  }
  if ((form.linkType === 'supplier' || hasSupplier) && !hasSupplier) {
    errors.supplierId = 'Seleziona un fornitore';
  }
  if (hasSupplier && !String(form.supplierName || '').trim()) {
    errors.supplierId = 'Seleziona un fornitore valido';
  }

  return errors;
}

export function buildAssignmentPayload({
  form,
  assignments,
  staff,
  currentUser,
}) {
  const recipients = form.recipientIds
    .map((id) => staff.find((member) => idsMatch(member.linkedWorkspaceUserId, id)))
    .filter(Boolean)
    .map((member) => member.name);

  const nextId = Math.max(...assignments.map((assignment) => Number(assignment.id) || 0), 0) + 1;

  return {
    id: nextId,
    sender: currentUser.name,
    senderId: currentUser.id,
    recipients,
    recipientIds: form.recipientIds,
    title: form.title.trim(),
    description: form.description.trim(),
    type: form.type,
    urgency: form.urgency,
    dueDate: form.dueDate,
    status: form.recipientIds.length > 0 && form.recipientIds.every((id) => idsMatch(id, currentUser.id)) ? 'in_corso' : 'da_accettare',
    comments: [],
    attachments: [],
    createdAt: new Date().toISOString(),
  };
}

export function createAssignmentState({
  assignments = [],
  form,
  staff = [],
  currentUser,
}) {
  const assignment = buildAssignmentPayload({
    form,
    assignments,
    staff,
    currentUser,
  });

  return {
    assignments: [...assignments, assignment],
    assignment,
  };
}

export function removeAssignmentState(assignments = [], assignmentId) {
  const assignment = assignments.find((item) => item.id === assignmentId) || null;

  return {
    assignments: assignments.filter((item) => item.id !== assignmentId),
    assignment,
  };
}

export function updateAssignmentStatusState(assignments = [], assignmentId, status, extraFields = {}) {
  let assignment = null;

  const updatedAssignments = assignments.map((item) => {
    if (item.id !== assignmentId) return item;

    assignment = {
      ...item,
      status,
      ...extraFields,
    };

    return assignment;
  });

  return {
    assignments: updatedAssignments,
    assignment,
  };
}

export function calculateAssignmentsStats(assignments, currentUser, currentRecipientIds = []) {
  const operationalAssignments = (assignments || []).filter(isOperationalAssignment);
  const currentUserId = currentUser?.id ?? null;
  const comparableRecipientIds = currentRecipientIds.length
    ? currentRecipientIds
    : [currentUserId].filter((value) => value !== null && value !== undefined && value !== '');

  const myRequests = operationalAssignments.filter((assignment) => idsMatch(assignment.senderId, currentUserId) && !isSelfAssignedTask(assignment, currentUser));
  const myCompleted = myRequests.filter((assignment) => assignment.status === 'completato').length;
  const myInProgress = myRequests.filter((assignment) => assignment.status === 'in_corso').length;
  const myPending = myRequests.filter((assignment) => assignment.status === 'da_accettare').length;

  const receivedAssignments = operationalAssignments.filter((assignment) =>
    comparableRecipientIds.some((recipientId) => includesComparableId(assignment.recipientIds, recipientId))
  );
  const receivedTotal = receivedAssignments.length;
  const receivedCompleted = receivedAssignments.filter(
    (assignment) => assignment.status === 'completato'
  ).length;

  const completionRate = myRequests.length
    ? Math.round((myCompleted / myRequests.length) * 100)
    : 0;

  return {
    myTotal: myRequests.length,
    myCompleted,
    myInProgress,
    myPending,
    receivedTotal,
    receivedCompleted,
    completionRate,
    receivedCompletionRate: receivedTotal
      ? Math.round((receivedCompleted / receivedTotal) * 100)
      : 0,
    myRequestsSummary: `${myCompleted} completati, ${myInProgress} in corso`,
    receivedSummary: `${receivedCompleted} completati`,
    completionRateSummary: 'dei task completati',
    pendingSummary: 'task da processare',
  };
}
